/**
 * Document API Hooks
 * Refactored: Phase 1 - Factory Pattern
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import {
  Document,
  DocumentCreate,
  DocumentSortBy,
  BodyUploadDocumentApiDocumentsUploadPost,
  BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut,
  BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut,
} from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createMutationHook } from './factories';

// --- Types ---

// Using the API-generated type for permissions updates
type DocumentPermissionsUpdate = BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut;

interface UploadDocumentParams {
  file: File;
  title?: string;
  aclSettings?: {
    visibility?: string | null;
    purpose?: string | null;
    tags?: string | null;
    category?: string | null;
    shared_with_users?: string | string[] | null;
    shared_with_groups?: string | string[] | null;
    shared_with_roles?: string | null;
    retention_days?: number | null;
    legal_basis?: string | null;
  };
  textExtractionProvider?: string;
}

interface UpdateDocumentFileParams {
  documentId: string;
  file: File;
  title?: string;
  aclSettings?: {
    visibility?: string | null;
    purpose?: string | null;
    tags?: string | null;
  };
  textExtractionProvider?: string;
}

interface SuccessMessage {
  message: string;
}

interface DocumentsParams {
  skip?: number;
  limit?: number;
  search?: string;
  category?: string;
  source_type?: string;
  visibility?: string;
  owner_id?: string;
  sort_by?: DocumentSortBy;
  created_at_gte?: string;
  created_at_lte?: string;
  updated_at_gte?: string;
  updated_at_lte?: string;
  shared_with_group?: string;
  shared_with_user?: string;
  exclude_owned?: boolean;
  organizationId?: string;
}

// --- Query Hooks ---
const toCsv = (value: string | string[] | null | undefined): string | null | undefined => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(',') : null;
  }
  return value;
};

/**
 * Fetches documents list with filters and pagination.
 * Automatically organization-scoped. Superadmins can specify organizationId.
 * Returns paginated response with documents array and total count.
 */
export const useDocuments = createQueryHook<{ documents: Document[], total: number }, DocumentsParams>({
  queryKey: (params) => ['documents', params] as const,
  queryFn: (apiClient, params) => {
    const { organizationId, ...queryParams } = params;

    const headers: Record<string, string> = {};
    if (organizationId) {
      headers['X-Organization-Id'] = organizationId;
    }

    return apiClient.getDocumentsApiDocumentsGet(
      queryParams,
      { headers }
    ).then(response => response.data);
  },
  logContext: 'GET /api/documents',
  staleTime: QUERY_STALE_TIME.SHORT,
  onSuccess: (data) => {
    log.api.response('GET', '/api/documents', 200, {
      dataLength: data?.documents?.length ?? 0,
      total: data?.total ?? 0,
      result: data
    });
  },
});

/**
 * Fetches a single document by ID.
 */
export const useDocument = createQueryHook<Document, string>({
  queryKey: (documentId) => ['documents', documentId] as const,
  queryFn: (apiClient, documentId) =>
    apiClient.getDocumentApiDocumentsDocumentIdGet(documentId).then(response => response.data),
  logContext: 'GET /api/documents/:id',
  staleTime: QUERY_STALE_TIME.LONG,
});

/**
 * Fetches available text extraction providers.
 */
export const useTextExtractionProviders = createQueryHook<string[], void>({
  queryKey: () => ['text-extraction', 'providers'] as const,
  queryFn: (apiClient) =>
    apiClient.getAvailableProvidersApiAdminTextExtractionConfigProvidersGet().then(response =>
      response.data
        .map(provider => (typeof provider.label === 'string' ? provider.label : undefined))
        .filter((label): label is string => Boolean(label))
    ),
  logContext: 'GET /api/admin/text-extraction-config/providers',
  staleTime: QUERY_STALE_TIME.LONG,
});

// --- Mutation Hooks ---

/**
 * Creates a new document from text content.
 */
export const useCreateDocument = createMutationHook<Document, DocumentCreate>({
  mutationFn: (apiClient, documentData) =>
    apiClient.createDocumentApiDocumentsPost(documentData).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'POST /api/documents',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_created', 'useDocuments');
  },
});

/**
 * Uploads a document file.
 */
export const useUploadDocument = createMutationHook<Document, UploadDocumentParams>({
  mutationFn: (apiClient, params) => {
    const { file, title, aclSettings, textExtractionProvider } = params;

    const uploadData: BodyUploadDocumentApiDocumentsUploadPost = {
      file,
      title: title ?? null,
      category: aclSettings?.category,
      tags: aclSettings?.tags,
      visibility: aclSettings?.visibility,
      purpose: aclSettings?.purpose,
      shared_with_users: toCsv(aclSettings?.shared_with_users),
      shared_with_groups: toCsv(aclSettings?.shared_with_groups),
      shared_with_roles: aclSettings?.shared_with_roles,
      retention_days: aclSettings?.retention_days,
      legal_basis: aclSettings?.legal_basis,
    };

    const payload = textExtractionProvider
      ? ({ ...uploadData, text_extraction_provider: textExtractionProvider } as BodyUploadDocumentApiDocumentsUploadPost)
      : uploadData;

    return apiClient.uploadDocumentApiDocumentsUploadPost(payload).then(response => response.data);
  },
  invalidateQueries: [['documents']],
  logContext: 'POST /api/documents/upload',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('document_uploaded', 'useDocuments');
  },
});

/**
 * Updates an existing document.
 * Note: The API expects DocumentCreate type for updates
 */
export const useUpdateDocument = createMutationHook<
  Document,
  { documentId: string; data: DocumentCreate }
>({
  mutationFn: (apiClient, { documentId, data }) =>
    apiClient.updateDocumentApiDocumentsDocumentIdPut(documentId, data).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Updates a document file.
 */
export const useUpdateDocumentFile = createMutationHook<Document, UpdateDocumentFileParams>({
  mutationFn: (apiClient, params) => {
    const { documentId, file, title, aclSettings, textExtractionProvider } = params;

    const uploadData: BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut = {
      file,
      title: title ?? null,
      visibility: aclSettings?.visibility,
      purpose: aclSettings?.purpose,
      tags: aclSettings?.tags,
    };

    const payload = textExtractionProvider
      ? ({ ...uploadData, text_extraction_provider: textExtractionProvider } as BodyUpdateDocumentFileApiDocumentsDocumentIdFilePut)
      : uploadData;

    return apiClient.updateDocumentFileApiDocumentsDocumentIdFilePut(documentId, payload).then(response => response.data);
  },
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/file',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('document_file_updated', 'useDocuments', { documentId: variables.documentId });
  },
});

/**
 * Deletes a document.
 */
export const useDeleteDocument = createMutationHook<SuccessMessage, string>({
  mutationFn: (apiClient, documentId) =>
    apiClient.deleteDocumentApiDocumentsDocumentIdDelete(documentId).then(response => response.data),
  invalidateQueries: [['documents']],
  removeQueries: [],
  logContext: 'DELETE /api/documents/:id',
  logMethod: 'DELETE',
  onSuccess: (_data, documentId) => {
    log.user.action('document_deleted', 'useDocuments', { documentId });
  },
});

/**
 * Updates document permissions.
 */
export const useUpdateDocumentPermissions = createMutationHook<
  Document,
  { documentId: string; permissions: DocumentPermissionsUpdate }
>({
  mutationFn: (apiClient, { documentId, permissions }) =>
    apiClient.updateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut(documentId, permissions).then(response => response.data),
  invalidateQueries: [['documents']],
  logContext: 'PUT /api/documents/:id/permissions',
  logMethod: 'PUT',
  onSuccess: (_data, variables) => {
    log.user.action('document_permissions_updated', 'useDocuments', { documentId: variables.documentId });
  },
});
